import React from 'react';
import { Modal } from '@odin-labs/components';
import { useRevokeDeveloperJobsiteAccessGrantMutation } from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { TrashIcon } from 'components/icons';
import { useIsMounted } from 'utils';
import { getGraphQLError } from 'utils/error';
import { DataSharingGrant } from 'containers/jobsiteConfiguration/tabs/dataSharing/types';

export type RevokeDataSharingGrantModalProps = {
  isOpen: boolean;
  grant: DataSharingGrant;
  onCancel: () => void;
  onConfirm: () => void;
};

export function RevokeDataSharingGrantModal(
  props: RevokeDataSharingGrantModalProps,
): React.ReactElement {
  const { isOpen, grant, onCancel, onConfirm } = props;

  const isMounted = useIsMounted();
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  const [revokeGrant] = useRevokeDeveloperJobsiteAccessGrantMutation({
    fetchPolicy: 'no-cache',
  });

  const onAction = async (): Promise<void> => {
    if (isSaving || !grant) return;
    setIsSaving(true);

    try {
      await revokeGrant({ variables: { input: { id: grant.id } } });
      onConfirm();
      AlertService.alert('success', 'Success', 'Data sharing grant revoked');
    } catch (error) {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    } finally {
      if (isMounted()) setIsSaving(false);
    }
  };

  const clientName = grant?.granteeDeveloper?.name;

  return (
    <Modal
      open={isOpen}
      setOpen={onCancel}
      size="xs"
      title={'Are you sure you want \nto revoke this grant?'}
      titleAlignment="center"
      text={
        clientName
          ? `${clientName} will lose access to this site’s data through the Client API.`
          : 'The client will lose access to this site’s data through the Client API.'
      }
      textAlignment="center"
      onAction={onAction}
      actionText="Revoke access"
      actionButtonTheme="danger"
      actionIcon={TrashIcon}
      actionButtonWithSpinner={isSaving}
    />
  );
}
